import React from 'react';
import { Card } from '@/components/shared/card';
import ValueSlider from './ValueSlider';
import { Weights } from '@/types/opportunity';

interface ValueDimensionsCardProps {
  weights: Weights;
  onWeightChange: (key: keyof Weights, value: number[]) => void;
}

const valueDimensions = [
  { 
    key: 'SpeedUpPermitting', 
    label: 'Speed Up Permitting',
    subtext: "Reduce the time it takes to process and approve permits through automation and streamlined workflows"
  },
  { 
    key: 'IncreaseStaffProductivity', 
    label: 'Increase Staff Productivity',
    subtext: "Enable staff to handle more applications and tasks efficiently through AI-assisted workflows"
  },
  { 
    key: 'SimplifyApplicationExperience', 
    label: 'Simplify Application Experience',
    subtext: "Make it easier for applicants to submit complete and accurate applications through guided processes"
  },
  { 
    key: 'EnhanceEquitableAccessToServices', 
    label: 'Enhance Equitable Access',
    subtext: "Improve service accessibility for all community members regardless of technical expertise or language"
  },
  { 
    key: 'StrengthenInternalVisibilityDecisionMaking', 
    label: 'Strengthen Internal Visibility',
    subtext: "Gain better insights into operations and enable data-driven decision making through analytics"
  },
  { 
    key: 'IncreaseProcessTransparency', 
    label: 'Increase Process Transparency',
    subtext: "Provide clearer visibility into permit status and requirements for both staff and applicants"
  },
  { 
    key: 'BoostFinancialSustainability', 
    label: 'Boost Financial Sustainability',
    subtext: "Improve revenue collection and reduce operational costs through process optimization"
  },
];

const ValueDimensionsCard = ({ weights, onWeightChange }: ValueDimensionsCardProps) => {
  return (
    <Card className="p-4 sm:p-6 lg:p-8 relative flex flex-col h-full">
      <span className="absolute top-4 sm:top-6 lg:top-8 left-4 sm:left-6 lg:left-8 px-3 sm:px-5 py-1 sm:py-2 font-medium text-xs sm:text-sm text-[#3B50A2] bg-gray-200 rounded-full text-center font-geist-mono">
        STEP 1
      </span>
      <h3 className="text-2xl sm:text-3xl font-medium mb-3 sm:mb-4 mt-8 sm:mt-10 lg:mt-12">What are your most important goals?</h3>
      <p className="text-sm sm:text-base lg:text-lg text-gray-600 mb-6 sm:mb-8 font-light min-h-[60px] sm:min-h-[80px] max-h-[80px] leading-relaxed">
      Drag the slider to the right to indicate a <b>higher priority</b>. We'll
use your responses to help identify AI solutions that align with
your goals.      
      </p>
      <div className="space-y-4 sm:space-y-6">
        {valueDimensions.map(({ key, label, subtext }, index) => (
          <ValueSlider
            key={key}
            label={label}
            subtext={subtext}
            value={weights[key as keyof typeof weights]}
            onChange={(value) => onWeightChange(key as keyof typeof weights, value)}
            isLastItem={index === valueDimensions.length - 1}
          />
        ))}
      </div>
    </Card>
  );
};

export default ValueDimensionsCard;